
'use client';

import { useCallback, useEffect, useState, useRef } from 'react';
import type { Birthday, BirthdayInput } from '@/lib/types';
import { useToast } from './use-toast';
import {
  useFirestore,
  useUser,
  useMemoFirebase,
  useAuth,
} from '@/firebase';
import {
  collection,
  doc,
  serverTimestamp,
  onSnapshot,
  Query,
  Unsubscribe,
} from 'firebase/firestore';
import {
  addDocumentNonBlocking,
  deleteDocumentNonBlocking,
  setDocumentNonBlocking,
} from '@/firebase/non-blocking-updates';
import { signInAnonymously } from 'firebase/auth';
import { requestForToken } from './use-notifications';
import { useFirebase } from '@/firebase';


async function ensureAuthenticated(auth: any, user: any) {
  if (user) {
    return user;
  }
  const userCredential = await signInAnonymously(auth);
  return userCredential.user;
}

export function useBirthdays() {
  const { toast } = useToast();
  const auth = useAuth();
  const { firebaseApp } = useFirebase();
  const firestore = useFirestore();
  const { user, isUserLoading } = useUser();
  const [birthdays, setBirthdays] = useState<Birthday[]>([]);
  const unsubscribeRef = useRef<Unsubscribe | null>(null);

  const VAPID_KEY = 'BJMooG-g2BEUwiM0QAGZ55VY49xF9UD4a2j3gJFjqlSJ1vFqLZ6chpT5JGRXXn4B2nMbBsy-6BwCv5mcarmUQbM';

  const saveFCMToken = useCallback(
    async (token: string) => {
      if (!firestore || !user) return;
      try {
        const userDocRef = doc(firestore, 'users', user.uid);
        setDocumentNonBlocking(userDocRef, { fcmToken: token }, { merge: true });
        console.log('FCM Token saved to Firestore.');
      } catch (error) {
        console.error('Error saving FCM token:', error);
      }
    },
    [firestore, user]
  );
  
  useEffect(() => {
    if (firebaseApp && user && Notification.permission === 'granted') {
      requestForToken(firebaseApp, VAPID_KEY, saveFCMToken);
    }
  }, [firebaseApp, user, saveFCMToken]);


  const birthdaysCollectionRef = useMemoFirebase(() => {
    if (!firestore || !user || isUserLoading) return null;
    return collection(firestore, 'users', user.uid, 'birthdays');
  }, [firestore, user, isUserLoading]);

  useEffect(() => {
    // If there's no collection reference, there's nothing to do.
    if (!birthdaysCollectionRef) {
      setBirthdays([]); // Clear data if user logs out
      return;
    }

    // Set up the real-time listener.
    const unsubscribe = onSnapshot(birthdaysCollectionRef as Query, (snapshot) => {
      const serverData = snapshot.docs.map(doc => ({ ...doc.data() as Birthday, id: doc.id }));
      setBirthdays(serverData);
    }, (error) => {
      console.error("Error listening to birthday updates:", error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not fetch birthday data.',
      });
    });

    // Return the unsubscribe function to be called on cleanup.
    return () => unsubscribe();

  }, [birthdaysCollectionRef, toast]); // Rerun effect if collection ref changes


  const addBirthday = useCallback(
    async (birthdayInput: BirthdayInput) => {
      try {
        const currentUser = await ensureAuthenticated(auth, user);
        if (!firestore || !currentUser) {
            throw new Error("User not authenticated.");
        }
        const ref = collection(firestore, 'users', currentUser.uid, 'birthdays');
        const newBirthday = {
            ...birthdayInput,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        };
        addDocumentNonBlocking(ref, newBirthday);
        toast({ title: '🎉 Birthday added!', description: `You've added ${birthdayInput.name}'s birthday.` });
      } catch (error) {
        console.error("Error adding birthday:", error);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Could not add birthday. Please try again.',
        });
      }
    },
    [auth, user, firestore, toast]
  );

  const updateBirthday = useCallback(
    async (updatedBirthday: Birthday) => {
      try {
        const currentUser = await ensureAuthenticated(auth, user);
        if (!firestore || !currentUser) {
            throw new Error("User not authenticated.");
        }
        const birthdayDocRef = doc(firestore, 'users', currentUser.uid, 'birthdays', updatedBirthday.id);
        const updateData = {
            ...updatedBirthday,
            updatedAt: serverTimestamp(),
        };
        const { id, ...dataToUpdate } = updateData;
        setDocumentNonBlocking(birthdayDocRef, dataToUpdate, { merge: true });
        toast({ title: '🎂 Birthday updated!', description: `${updatedBirthday.name}'s birthday has been updated.` });
      } catch (error) {
        console.error("Error updating birthday:", error);
        toast({
            variant: 'destructive',
            title: 'Error',
            description: 'Could not update birthday. Please try again.',
        });
      }
    },
    [auth, user, firestore, toast]
  );

  const deleteBirthday = useCallback(
    async (id: string) => {
      try {
        const currentUser = await ensureAuthenticated(auth, user);
        if (!firestore || !currentUser) {
            throw new Error("User not authenticated.");
        }
        const birthdayDocRef = doc(firestore, 'users', currentUser.uid, 'birthdays', id);
        deleteDocumentNonBlocking(birthdayDocRef);
        toast({ title: '🗑️ Birthday deleted.' });
       } catch (error) {
        console.error("Error deleting birthday:", error);
        toast({
            variant: 'destructive',
            title: 'Error',
            description: 'Could not delete birthday. Please try again.',
        });
      }
    },
    [auth, user, firestore, toast]
  );
  
  const getGiftSuggestion = useCallback((birthdayId: string): string[] | null => {
    // This is a placeholder as we are not caching gift suggestions in this version.
    return null;
  }, []);

  const setGiftSuggestion = useCallback((birthdayId: string, suggestions: string[]) => {
    // This is a placeholder as we are not caching gift suggestions in this version.
  }, []);


  return {
    birthdays: birthdays ?? [],
    addBirthday,
    updateBirthday,
    deleteBirthday,
    getGiftSuggestion,
    setGiftSuggestion,
  };
}
